import { Box, List, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  CampaignsConnectionFragment,
  EntryCampaignRankingConnectionFragment,
  Maybe,
  MissionsConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import {
  CampaignBox,
  CampaignBoxSkeleton,
  MissionBox,
  MissionBoxSkeleton,
} from '@/components/organisms'
import {
  ScrollFetcher,
  Segment,
  Tabs,
} from '@/components/organisms/functionals'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  hasMissionNextPage: boolean
  hasCampaignNextPage: boolean
  missions: Maybe<MissionsConnectionFragment>
  campaigns: Maybe<CampaignsConnectionFragment>
  campaignsParticipating: Maybe<
    Array<Maybe<EntryCampaignRankingConnectionFragment>>
  >
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
}

type CampaignsCellProps = {
  loading: boolean
  hasNextPage: boolean
  campaigns: Maybe<CampaignsConnectionFragment>
  onFetchMoreCampaign: Action['onFetchMoreCampaign']
}

type CampaignsParticipatingCellProps = {
  loading: boolean
  campaigns: Maybe<Array<Maybe<EntryCampaignRankingConnectionFragment>>>
}

type MissionsCellProps = {
  loading: boolean
  hasNextPage: boolean
  missions: Maybe<MissionsConnectionFragment>
  onFetchMoreMission: Action['onFetchMoreMission']
}

export type Action = {
  onFetchMoreCampaign: () => Promise<void>
  onFetchMoreMission: () => Promise<void>
}

export const GlobalEventsTemplate: Template<State, Action> = ({
  state,
  onFetchMoreCampaign,
  onFetchMoreMission,
}) => {
  const { t } = useTranslation('globalEvents')
  const { router, generatePath, routerPages } =
    useRouter<{ tab: string; artistId?: string }>()
  const { routerNames } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  const [currentTab, setCurrentTab] = useState<number>(
    router.query.tab === 'mission' ? 1 : 0
  )
  const [currentSegment, setCurrentSegment] = useState<number>(0)

  const handleTabChange = useCallback(
    (value: number) => {
      setCurrentTab(value)
      router.replace(
        generatePath(
          router.query.artistId
            ? routerPages.ArtistMissions
            : routerPages.GlobalEvents,
          {
            tab: value === 1 ? 'mission' : undefined,
            artistId: router.query.artistId,
          }
        )
      )
    },
    [generatePath, router, routerPages.ArtistMissions, routerPages.GlobalEvents]
  )

  const handleSegmentChange = useCallback((value: number) => {
    setCurrentSegment(value)
  }, [])

  return (
    <BaseLayout
      navigation={{
        type: 'global',
        props: {
          hasManagementTask: !!state.user?.hasManagementTask,
          unreadNotificationCount: state.user?.unreadNotificationCount,
          hasSettingTask:
            !!state?.user?.isMembershipPersonalInformationRequired,
        },
      }}
      header={{
        title: t('title'),
        back: config.applicationType === 'standalone',
        left: config.applicationType === 'organization' && (
          <IconButton.AvatarNoImage
            edge="start"
            onClick={() => setOpenSideMenu(!openSideMenu)}
            color="icon.line"
            bgcolor="icon.background"
            badge={
              state.user?.hasManagementTask ||
              !!state.user?.unreadNotificationCount ||
              !!state?.user?.isMembershipPersonalInformationRequired
            }
          />
        ),
      }}
    >
      <Head title={routerNames.GlobalEvents} />
      <SideMenu
        followArtists={state.followArtists}
        memberArtists={state.memberArtists}
        open={openSideMenu}
        isArtist={state.isArtist}
        onClose={() => setOpenSideMenu(false)}
        user={state.user}
      />
      <Tabs
        labels={[t('campaigns'), t('missions')]}
        value={currentTab}
        disableAnimateTransitions
        onChange={handleTabChange}
      >
        <Box sx={{ px: 5 }}>
          <Segment
            labels={[t('campaignsSession'), t('campaignsParticipating')]}
            value={currentSegment}
            disableAnimateTransitions
            onChange={handleSegmentChange}
          >
            <CampaignsCell
              loading={state.loading || state.fetching}
              hasNextPage={state.hasCampaignNextPage}
              campaigns={state.campaigns}
              onFetchMoreCampaign={onFetchMoreCampaign}
            />
            <CampaignsParticipatingCell
              loading={state.loading || state.fetching}
              campaigns={state.campaignsParticipating}
            />
          </Segment>
        </Box>
        <MissionsCell
          loading={state.loading}
          hasNextPage={state.hasMissionNextPage}
          missions={state.missions}
          onFetchMoreMission={onFetchMoreMission}
        />
      </Tabs>
    </BaseLayout>
  )
}

const MissionsCell: Cell<MissionsCellProps> = ({
  loading,
  hasNextPage,
  missions,
  onFetchMoreMission,
}) => {
  const { t } = useTranslation('globalEvents')
  const { analytics } = useAnalytics()
  return (
    <CellFrame sx={{ px: 5, mt: 4, pb: '64px' }}>
      <ScrollFetcher
        nodes={missions?.edges?.map(({ node }) => (
          <MissionBox
            fullWidth
            mission={node}
            key={node.id}
            withArtistName
            onClick={() => {
              analytics?.track('click_select', {
                applicationKey: config.applicationKey,
                missionId: node.id,
                name: 'eventMission',
              })
            }}
          />
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMoreMission}
        componentProps={{ spacing: 6 }}
        loadingComponent={
          <Stack spacing={6}>
            {[1, 2, 3, 4].map(index => (
              <MissionBoxSkeleton key={index} fullWidth />
            ))}
          </Stack>
        }
        emptyMessage={t('missionNotExist')}
      />
    </CellFrame>
  )
}

const CampaignsCell: Cell<CampaignsCellProps> = ({
  loading,
  hasNextPage,
  campaigns,
  onFetchMoreCampaign,
}) => {
  const { t } = useTranslation('globalEvents')
  const { analytics } = useAnalytics()
  return (
    <CellFrame>
      <ScrollFetcher
        nodes={campaigns?.edges.map(({ node }) => (
          <CampaignBox
            campaign={node}
            key={node.id}
            onClick={() => {
              analytics?.track('click_select', {
                applicationKey: config.applicationKey,
                campaignId: node.id,
                name: 'eventCampaign',
                tab: 'onhold',
              })
            }}
          />
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMoreCampaign}
        component={List}
        componentProps={{ spacing: 0 }}
        loadingComponent={
          <>
            {[1, 2, 3, 4].map(index => (
              <CampaignBoxSkeleton key={index} />
            ))}
          </>
        }
        emptyMessage={t('sessionCampaignNotExist')}
      />
    </CellFrame>
  )
}

const CampaignsParticipatingCell: Cell<CampaignsParticipatingCellProps> = ({
  loading,
  campaigns,
}) => {
  const { t } = useTranslation('globalEvents')
  const { analytics } = useAnalytics()
  return (
    <CellFrame>
      {loading ? (
        <List>
          {[1, 2, 3, 4].map(index => (
            <CampaignBoxSkeleton key={index} />
          ))}
        </List>
      ) : campaigns?.length ? (
        <List>
          {campaigns?.map(
            campaign =>
              campaign && (
                <CampaignBox
                  key={campaign.id}
                  campaign={campaign}
                  onClick={() => {
                    analytics?.track('click_select', {
                      applicationKey: config.applicationKey,
                      campaignId: campaign.id,
                      name: 'eventCampaign',
                      tab: 'participating',
                    })
                  }}
                />
              )
          )}
        </List>
      ) : (
        <Typography
          variant="body1"
          align="center"
          sx={{ py: 6 }}
          color="text.secondary"
        >
          {t('participatingCampaignNotExist')}
        </Typography>
      )}
    </CellFrame>
  )
}
